import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { actorProfiles } from "@/mocks/actors";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/feature/MovieCard";
import { useFavorites } from "@/hooks/useFavorites";
import { formatDate, formatViews } from "@/utils/format";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setToast("");
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((item) => item.id !== movie.id)
      .map((item) => {
        let score = 0;
        if (item.type === movie.type) score += 1;
        score += item.genres.filter((genre) => movie.genres.includes(genre)).length * 2;
        if (movie.actors.some((actor) => item.actors.some((other) => other.name === actor.name))) score += 3;
        return { item, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((entry) => entry.item);
  }, [movie]);

  usePageMeta(
    movie ? `${movie.title} - 诱人的女员工hd影视详情` : "影视作品详情 - 诱人的女员工hd影视专区",
    movie
      ? `${movie.title}：${movie.year}年${movie.region}${movie.type}，${movie.genres.join("、")}题材，${movie.director}执导，${movie.cast}主演。查看剧情简介、演员资料与相关推荐。`
      : "查看影视作品的剧情简介、演员资料与相关推荐。",
  );

  if (!movie) {
    return (
      <PageLayout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-film-line text-2xl" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-950">未找到该影视作品</h1>
          <p className="mt-2 text-sm text-foreground-500">作品可能已下线，返回分类页看看其他推荐。</p>
          <Link
            to="/category"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-950 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-900 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回影视分类
          </Link>
        </div>
      </PageLayout>
    );
  }

  const favorited = isFavorite(movie.id);

  const handleFavorite = () => {
    toggleFavorite(movie.id);
    setToast(favorited ? "已取消收藏" : "已加入本地收藏");
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("详情链接已复制");
    } catch {
      setToast("当前浏览器不支持自动复制");
    }
  };

  const infoRows = [
    { label: "上映年份", value: `${movie.year} 年` },
    { label: "内容类型", value: movie.type },
    { label: "制片地区", value: movie.region },
    { label: "影片时长", value: movie.duration },
    { label: "导演", value: movie.director },
    { label: "更新时间", value: formatDate(movie.updatedAt) },
  ];

  return (
    <PageLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-3 md:px-6">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center text-foreground-300" />
            <Link to="/category" className="hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">
              影视分类
            </Link>
            <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center text-foreground-300" />
            <span className="truncate text-foreground-700">{movie.title}</span>
          </nav>
        </div>
      </div>

      <article className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr] lg:gap-12">
          <div className="w-full max-w-[260px] lg:max-w-none">
            <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title}封面`}
                title={`${movie.title} ${movie.type} 剧情与演员信息`}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-3 top-3 rounded-sm bg-primary-950/85 px-2 py-1 text-[11px] text-background-50 backdrop-blur-sm">
                {movie.type}
              </span>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-4">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs text-foreground-500">观众评分</p>
                  <p className="mt-1 font-label text-3xl font-bold tracking-tight text-foreground-950">
                    {movie.rating.toFixed(1)}
                    <span className="ml-1 text-xs font-normal text-foreground-400">/ 10</span>
                  </p>
                </div>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={`${
                        star <= Math.round(movie.rating / 2) ? "ri-star-fill text-accent-500" : "ri-star-line text-background-400"
                      } text-sm`}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                <span>播放 {formatViews(movie.views)}</span>
                <span>{formatDate(movie.updatedAt)} 更新</span>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleFavorite}
                aria-pressed={favorited}
                className={`flex items-center justify-center gap-1.5 rounded-md py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? "bg-accent-500 text-primary-950 font-medium hover:bg-accent-400"
                    : "border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
                }`}
              >
                <i className={`${favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} w-4 h-4 flex items-center justify-center`} />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center justify-center gap-1.5 rounded-md border border-background-300 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center" />
                分享
              </button>
            </div>
            {toast && (
              <p className="mt-2 rounded-md bg-secondary-100 px-3 py-2 text-center text-xs text-secondary-900">{toast}</p>
            )}
            <p className="mt-3 text-[11px] leading-5 text-foreground-400">
              收藏仅保存在当前浏览器的本地记录中，不同设备之间不会同步。
            </p>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {movie.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category?genre=${encodeURIComponent(genre)}`}
                  className="rounded-sm bg-secondary-100 px-2 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                >
                  {genre}
                </Link>
              ))}
              <span className="rounded-sm bg-background-200 px-2 py-1 text-[11px] text-foreground-600">
                {movie.region} · {movie.year}
              </span>
            </div>

            <h1 className="mt-4 text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">{movie.title}</h1>
            <p className="mt-3 text-sm text-accent-700 md:text-base">「{movie.tagline}」</p>
            <p className="mt-4 text-sm leading-7 text-foreground-600">{movie.summary}</p>

            <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-4 rounded-lg border border-background-200 bg-background-100 p-4 sm:grid-cols-3 md:p-5">
              {infoRows.map((row) => (
                <div key={row.label} className="min-w-0">
                  <p className="text-[11px] text-foreground-500">{row.label}</p>
                  <p className="mt-1 truncate text-sm font-medium text-foreground-950">{row.value}</p>
                </div>
              ))}
              <div className="col-span-2 min-w-0 sm:col-span-3">
                <p className="text-[11px] text-foreground-500">主演</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {movie.actors.map((actor) => (
                    <span
                      key={actor.name}
                      className="rounded-sm bg-background-50 px-2 py-1 text-xs text-foreground-800 border border-background-200"
                    >
                      {actor.name}
                      <span className="text-foreground-400"> 饰 {actor.role}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <section className="mt-8" aria-labelledby="synopsis-title">
              <h2 id="synopsis-title" className="flex items-center gap-2 text-lg font-bold tracking-tight text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                剧情简介
              </h2>
              <div className="mt-4 space-y-4">
                {movie.synopsis.map((paragraph) => (
                  <p key={paragraph.slice(0, 12)} className="text-sm leading-8 text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>
              <p className="mt-4 rounded-md bg-background-100 px-4 py-3 text-xs leading-6 text-foreground-500">
                以上剧情与人物均为本站示范性虚构内容，仅用于展示影视资讯结构，不代表任何真实作品。
              </p>
            </section>
          </div>
        </div>
      </article>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <h2 className="flex items-center gap-2 text-lg font-bold tracking-tight text-foreground-950 md:text-xl">
            <span className="h-4 w-1 rounded-full bg-accent-500" />
            演员信息
          </h2>
          <p className="mt-2 text-sm text-foreground-600">主要演员与角色对照，附带简短人气点评。</p>

          <div className="mt-6 flex gap-4 overflow-x-auto pb-2 no-scrollbar md:grid md:grid-cols-3 md:overflow-visible md:pb-0 lg:grid-cols-3">
            {movie.actors.map((actor, actorIndex) => {
              const profile = actorProfiles[actor.name];
              return (
                <div
                  key={actor.name}
                  className="flex w-[260px] shrink-0 gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:w-auto"
                >
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={profile?.avatar}
                      alt={`${actor.name}演员照片`}
                      title={`${actor.name} 演员资料`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-sm font-medium text-foreground-950">{actor.name}</h3>
                      <span className="font-label text-[10px] text-accent-600">{(9.4 - actorIndex * 0.3).toFixed(1)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-foreground-500">饰 {actor.role}</p>
                    <p className="mt-1.5 text-xs leading-6 text-foreground-600 line-clamp-3">
                      {profile?.intro ?? "暂无演员资料。"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold tracking-tight text-foreground-950 md:text-xl">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              相关推荐
            </h2>
            <p className="mt-2 text-sm text-foreground-600">根据题材与演员匹配的其它作品，风格相近可以一起看。</p>
          </div>
          <Link
            to={`/category?genre=${encodeURIComponent(movie.genres[0])}`}
            className="inline-flex items-center gap-1 text-sm text-foreground-600 transition-colors hover:text-accent-600 cursor-pointer whitespace-nowrap"
          >
            更多{movie.genres[0]}作品
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
          {related.map((item) => (
            <MovieCard key={item.id} movie={item} />
          ))}
        </div>
      </section>
    </PageLayout>
  );
}